import type { CostType, EventType } from './enums';

export interface SeedCategory {
  systemKey: string;
  name: string;
  nameAm: string; // draft — reviewed by the owner (sprint 4 §5)
  costType: CostType;
  isFeed: boolean;
  parentKey: string | null;
}

const group = (systemKey: string, name: string, nameAm: string, costType: CostType = 'OPERATING'): SeedCategory => ({
  systemKey,
  name,
  nameAm,
  costType,
  isFeed: false,
  parentKey: null,
});

const leaf = (
  parentKey: string,
  systemKey: string,
  name: string,
  nameAm: string,
  costType: CostType = 'OPERATING',
  isFeed = false,
): SeedCategory => ({ systemKey, name, nameAm, costType, isFeed, parentKey });

/** Sprint 4 §5 — seeded for every farm, idempotent by systemKey. Leaves inherit the group's cost type. */
export const SEEDED_CATEGORIES: SeedCategory[] = [
  group('feed', 'Feed', 'መኖ'),
  leaf('feed', 'feed.concentrate', 'Concentrate', 'የተቀነባበረ መኖ', 'OPERATING', true),
  leaf('feed', 'feed.hay', 'Hay', 'ድርቆሽ', 'OPERATING', true),
  leaf('feed', 'feed.bran', 'Wheat bran (frushka)', 'ፉርሽካ', 'OPERATING', true),
  leaf('feed', 'feed.oilcake', 'Oilseed cake (fagulo)', 'ፋጉሎ', 'OPERATING', true),
  leaf('feed', 'feed.byproducts', "Brewery/distillery by-products (atela, brewers' grain)", 'አተላ እና ብቅል', 'OPERATING', true),
  leaf('feed', 'feed.green', 'Silage and green fodder', 'ሲላጅ እና አረንጓዴ መኖ', 'OPERATING', true),
  leaf('feed', 'feed.minerals', 'Minerals and salt', 'ማዕድን እና ጨው', 'OPERATING', true),
  group('labor', 'Labor', 'የሰው ኃይል'),
  leaf('labor', 'labor.salary', 'Salaries', 'ደመወዝ'),
  leaf('labor', 'labor.casual', 'Casual labor', 'የቀን ሠራተኛ'),
  group('vet', 'Vet and breeding', 'ሕክምና እና ማዳቀል'),
  leaf('vet', 'vet.ai', 'AI service', 'ሰው ሰራሽ ማዳቀል'),
  leaf('vet', 'vet.services', 'Vet services and checks', 'የእንስሳት ሐኪም አገልግሎት'),
  leaf('vet', 'vet.drugs', 'Drugs and treatment', 'መድኃኒት'),
  leaf('vet', 'vet.vaccines', 'Vaccines and dewormers', 'ክትባት እና የውስጥ ጥገኛ መድኃኒት'),
  group('utilities', 'Utilities', 'አገልግሎቶች'),
  leaf('utilities', 'utilities.water', 'Water', 'ውሃ'),
  leaf('utilities', 'utilities.electricity', 'Electricity', 'መብራት'),
  leaf('utilities', 'utilities.fuel', 'Fuel', 'ነዳጅ'),
  group('transport', 'Transport', 'ትራንስፖርት'),
  leaf('transport', 'transport.general', 'Transport', 'ትራንስፖርት'),
  group('supplies', 'Supplies and maintenance', 'አቅርቦት እና ጥገና'),
  leaf('supplies', 'supplies.milking', 'Milking supplies and cleaning', 'የማለቢያ ዕቃዎች እና ንጽሕና'),
  leaf('supplies', 'supplies.repairs', 'Repairs', 'ጥገና'),
  group('rent', 'Rent and fees', 'ኪራይ እና ክፍያዎች'),
  leaf('rent', 'rent.rent', 'Rent', 'ኪራይ'),
  leaf('rent', 'rent.fees', 'Taxes and fees', 'ግብር እና ክፍያዎች'),
  group('capital', 'Investments', 'ኢንቨስትመንት', 'CAPITAL'),
  leaf('capital', 'capital.animals', 'Animal purchase', 'የእንስሳት ግዢ', 'CAPITAL'),
  leaf('capital', 'capital.equipment', 'Equipment', 'መሣሪያ', 'CAPITAL'),
  leaf('capital', 'capital.construction', 'Construction', 'ግንባታ', 'CAPITAL'),
  group('other', 'Other', 'ሌላ'),
  leaf('other', 'other.other', 'Other', 'ሌላ'),
];

/** EX-3: event cost → category systemKey. */
export const EVENT_COST_CATEGORY: Partial<Record<EventType, string>> = {
  SERVICE: 'vet.ai',
  PREG_CHECK: 'vet.services',
  TREATMENT: 'vet.drugs',
  VACCINATION: 'vet.vaccines',
  DEWORMING: 'vet.vaccines',
};
